import { useCallback } from "react";
import { Stack, StackProps } from "@chakra-ui/react";
import { useTranslation } from "gatsby-plugin-react-i18next";

import { useSlide } from "../public/slide";
import { SlidePropsMulti, SelectorValue } from "../public/types";
import { CommonSelect } from "./commonSelect";
import { MultiState, useQuizActions, useQuizSnapshot } from "./state";

import { MyFormLabel } from "./ui";

export type MultiSelectProps = {
  /** Overrides the default click behavior (set value + auto-submit + advance). */
  onOptionClick?: (value: SelectorValue) => void;
  /** Props for the root container of the selector */
  containerProps?: StackProps;
  /** Color of the "(Choose one or more)" label */
  labelColor?: string;
  /** Hides the "(Choose one or more)" label */
  hideLabel?: boolean;
} & SlidePropsMulti;

export function MultiSelect(props: MultiSelectProps) {
  const actions = useQuizActions();
  const slideCtx = useSlide();
  const snap = useQuizSnapshot();
  const { t } = useTranslation();
  const { onOptionClick } = props;

  const handleClick = useCallback(
    (value: SelectorValue) => {
      if (onOptionClick) {
        onOptionClick(value);
        return;
      }
      actions.toggleMultiOption(slideCtx.id, value);
    },
    [onOptionClick],
  );

  const selectorState = snap.slideStateByID[slideCtx.id] as MultiState | undefined;

  return (
    <Stack spacing={1} {...props.containerProps}>
      {!props.hideLabel && (
        <MyFormLabel mx="auto" color={props.labelColor}>
          {props.label ??
            t("quizLib.multi.chooseOneOrMore", { defaultValue: "(Choose one or more)" })}{" "}
        </MyFormLabel>
      )}
      <CommonSelect
        handleOptionClick={handleClick}
        isOptionSelected={(o) => selectorState?.value?.some((v) => v.value === o.value) ?? false}
        maxItems={props.maxItems}
      />
    </Stack>
  );
}
